import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { OptionItem } from "@/api/admin";
import { DINERS_STATUS_VARIANT_MAP } from "@/api/admin/diners";
import {
  type RestaurantDinerType,
  useGetRestaurantAllBranchDinersQuery,
  useGetRestaurantAllBrandsDinersQuery,
  useGetRestaurantBranchDinersQuery,
  useGetRestaurantBrandDinersQuery,
  useGetRestaurantGroupDinersQuery,
} from "@/api/restaurant/diners";
import {
  Loader,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  TableHeadCellSort,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import { PATHS } from "@/config/path";
import { rootStore } from "@/store/RootStore";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";
import { createSortHandler } from "@/utils/createSortHandler";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useTableParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";
import { parseAmountRange } from "@/utils/parseAmountRange";
import { parseDateRangeToParams } from "@/utils/parseDateRangeToParams";
import { renderMoneyValue } from "@/utils/renderMoneyValue";

import DynamicTable from "../DynamicTable";
import type { ColumnConfig } from "../type";

enum CustomersColumn {
  NAME = "Name",
  LAST_VISIT = "Last visit",
  TOTAL_ORDERS = "Total orders",
  TOTAL_SPEND = "Total spend",
}

enum Mode {
  BRAND_ALL = "brandAll",
  BRANCH_ALL = "branchAll",
  GROUP = "group",
  BRAND = "brand",
  BRANCH = "branch",
}

const RestaurantDinersTable = observer(() => {
  const [searchValue, setSearchValue] = useState("");

  const { tableParams, handleNext, handlePrev, setTableParams } =
    useTableParams(
      {
        initialParams: { query: "" },
      },
      searchValue
    );

  const { isBranchAccess, isBrandAccess } = rootStore.authStore;

  const {
    selectedBrand,
    selectedBranch,
    isAllBrandsSelected,
    isAllBranchesSelected,
    selectedGroup,
  } = rootStore.restaurantAccessStore;

  const mode = (() => {
    if (isAllBrandsSelected) return Mode.BRAND_ALL;
    if (isAllBranchesSelected) return Mode.BRANCH_ALL;
    if (!selectedBrand) return Mode.GROUP;
    if (!selectedBranch) return Mode.BRAND;
    return Mode.BRANCH;
  })();

  const groupRes = useGetRestaurantGroupDinersQuery(
    selectedGroup?.id || "",
    tableParams,
    mode === Mode.GROUP
  );
  const brandRes = useGetRestaurantBrandDinersQuery(
    selectedBrand?.id || "",
    tableParams,
    mode === Mode.BRAND
  );
  const branchRes = useGetRestaurantBranchDinersQuery(
    selectedBranch?.id || "",
    tableParams,
    mode === Mode.BRANCH
  );
  const allBrandsRes = useGetRestaurantAllBrandsDinersQuery(
    tableParams,
    mode === Mode.BRAND_ALL
  );
  const allBranchesRes = useGetRestaurantAllBranchDinersQuery(
    tableParams,
    mode === Mode.BRANCH_ALL
  );

  const queryMap = {
    group: groupRes,
    brand: brandRes,
    branch: branchRes,
    brandAll: allBrandsRes,
    branchAll: allBranchesRes,
  };

  const { data: tableData, isPending } = queryMap[mode];

  const handleSortLastVisit = createSortHandler(setTableParams, "lastVisit");
  const handleSortTotalOrders = createSortHandler(
    setTableParams,
    "totalOrders"
  );
  const handleSortTotalSpend = createSortHandler(setTableParams, "totalSpent");

  const TABLE_HEADERS: TableHeaderType = Object.values(CustomersColumn);

  const SUB_MENUS = {
    [CustomersColumn.LAST_VISIT]: [],
    [CustomersColumn.TOTAL_ORDERS]: [],
    [CustomersColumn.TOTAL_SPEND]: [],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    [CustomersColumn.LAST_VISIT]: { type: "date" },
    [CustomersColumn.TOTAL_ORDERS]: {
      type: "amount",
      range: true,
    },
    [CustomersColumn.TOTAL_SPEND]: {
      type: "amount",
      range: true,
    },
  };

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const { dateFrom: lastVisitFrom, dateTo: lastVisitTo } =
      parseDateRangeToParams(
        selectedMap[CustomersColumn.LAST_VISIT]?.[0],
        true
      );

    const { amountFrom: ordersCountFrom, amountTo: ordersCountTo } =
      parseAmountRange(selectedMap[CustomersColumn.TOTAL_ORDERS]?.[0]);

    const { amountFrom: totalSpentFrom, amountTo: totalSpentTo } =
      parseAmountRange(selectedMap[CustomersColumn.TOTAL_SPEND]?.[0]);

    setTableParams(prev => ({
      ...prev,
      ordersCountFrom,
      ordersCountTo,
      totalSpentFrom,
      totalSpentTo,
      lastVisitFrom,
      lastVisitTo,
      offset: 0,
    }));
  };

  const dinerPathFactory: (id: string) => string = (() => {
    if (isBranchAccess) {
      return PATHS.RESTAURANT.BRANCH.ONE_DINER;
    }

    if (isBrandAccess) {
      return PATHS.RESTAURANT.BRAND.ONE_DINER;
    }

    return PATHS.RESTAURANT.GROUP.ONE_DINER;
  })();

  const columns: ColumnConfig<RestaurantDinerType>[] = [
    {
      key: "name",
      label: CustomersColumn.NAME,
      visible: flags[CustomersColumn.NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellLink last={isLast}>
          <Link
            to={dinerPathFactory(row.id?.toString() || "")}
            data-testid={`restaurant-diner-cell-id-${row.id}`}
          >
            {row?.name || DEFAULT_NAME}
          </Link>
        </TableCellLink>
      ),
    },
    {
      key: "lastVisit",
      label: CustomersColumn.LAST_VISIT,
      visible: flags[CustomersColumn.LAST_VISIT],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortLastVisit}
          field="lastVisit"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.lastVisit) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "totalOrders",
      label: CustomersColumn.TOTAL_ORDERS,
      visible: flags[CustomersColumn.TOTAL_ORDERS],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortTotalOrders}
          field="totalOrders"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row?.totalOrders?.toString() ?? "-"}
        </TableCellText>
      ),
    },
    {
      key: "totalSpent",
      label: CustomersColumn.TOTAL_SPEND,
      visible: flags[CustomersColumn.TOTAL_SPEND],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortTotalSpend}
          field="totalSpent"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {renderMoneyValue(row?.totalSpent.toString()) ?? "-"}
        </TableCellText>
      ),
    },
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
        statusVariant={DINERS_STATUS_VARIANT_MAP}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isPending ? (
        <Loader />
      ) : (
        <DynamicTable<RestaurantDinerType>
          data={tableData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={tableData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
});

export default RestaurantDinersTable;
